// Global
import React from 'react';
import Head from 'next/head';

// Local
import { Content } from '@generated/Content/Accordion.model';
import { Accordion } from './Accordion';
import PlainTextWrapper from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import ImageWrapper from 'components/helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { getComponentCss } from 'lib/utils/sitecore/component-css';
import { usePreambleFields } from 'components/helpers/Molecules/Preamble/Preamble.sitecore';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { buildFaqJsonLd } from 'lib/seo/seoComponentsSchema';
import { Themes } from 'lib/themes';

type AccordionProps = Content.Accordion.Accordion_Component;

export const Default = (props: AccordionProps) => {
  /*
   * Data Processing
   */

  const preambleFields = usePreambleFields({
    fields: props.fields,
  });

  const isEditing = useIsEditing();
  const theme = (
    props.params?.theme ?? (props.params as Record<string, string> | undefined)?.Theme
  )?.trim() as Themes;

  // In editing mode, always render the component even if empty so it can be edited
  if (!props.fields?.accordionItems || props.fields.accordionItems.length === 0) {
    if (isEditing) {
      return (
        <Accordion
          className={getComponentCss(props)}
          preambleFields={preambleFields}
          accordionItems={[]}
          theme={theme}
        />
      );
    }
    return <></>;
  }

  // Check if hideAllImages parameter is set
  const hideAllImages = props.params?.hideAllImages === '1';

  // Map the multilist items to accordion item data for helper Accordion
  const accordionItems = (props.fields.accordionItems as Content.Accordion.AccordionItems_Item[])
    .filter((item) => {
      // Filter out items without required fields
      return !!(item?.fields?.heading && item?.fields?.content);
    })
    .map((item, index) => ({
      uid: item.id || index.toString(),
      heading: <PlainTextWrapper field={item.fields?.heading} />,
      content: <RichTextWrapper field={item.fields?.content} />,
      icon:
        !hideAllImages && item.fields?.image?.value?.src ? (
          <ImageWrapper field={item.fields.image} />
        ) : !hideAllImages && isEditing && item.fields?.image ? (
          <ImageWrapper field={item.fields.image} />
        ) : undefined,
    }));

  // Check if none of the items have images (for layout adjustment)
  const noImagesExist = !hideAllImages && accordionItems.every((item) => !item.icon);

  // Build FAQ JSON-LD if enableFaqMode is checked
  const enableFaqSchema = props.fields?.enableFaqMode?.value;
  const qaPairs = enableFaqSchema
    ? (props.fields.accordionItems as Content.Accordion.AccordionItems_Item[])
        .map((item) => ({
          question: item?.fields?.heading?.value?.toString().trim() || '',
          answer: item?.fields?.content?.value?.toString().trim() || '',
        }))
        .filter((x) => x.question.length > 0 && x.answer.length > 0)
    : [];

  const faqJsonLd = enableFaqSchema && qaPairs.length > 0 ? buildFaqJsonLd(qaPairs) : null;

  return (
    <>
      {faqJsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
          />
        </Head>
      )}
      <Accordion
        className={getComponentCss(props)}
        preambleFields={preambleFields}
        accordionItems={accordionItems}
        noImagesLayout={noImagesExist}
        theme={theme}
      />
    </>
  );
};
